import { ITestConnectionResult, EditableConnection, CredentialOption } from '~src/connectivity/services/EditConnectionDialog';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IActionResult } from '~src/ts/ServerApi/types/IActionResult';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { CommonErrorCode, DataConnectionErrorCode } from '~src/ts/helpers/ErrorCodes';
import { DataConnections, IConnectionUpdate } from '~src/connectivity/resources/DataConnections';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { Formatters, Messages } from '~src/codegen/Localize';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IDataConnectionEditModelReact, ITestConnectionResultReact } from './EditOAuthConnectionActionTypes';
import { ICheckConnectionResult } from '~src/ts/ServerApi/requests/CheckConnectionRequest';
import { L10n } from '~src/ts/L10n/L10n';

export namespace EditOAuthConnectionUtils {
  export async function checkOAuthConnection(
    oauthCredentialId: string,
  ): Promise<ITestConnectionResult> {
    try {
      const result = await OAuthCredentials.test(oauthCredentialId);
      return {
        succeeded: !!result,
        isPending: false,
      };
    } catch (e) {
      return {
        succeeded: false,
        isPending: false
      };
    }
  }

  export async function updateOAuthConnectionsToServer(connections: EditableConnection[],
    credentialOption: CredentialOption,
    username: string,
    password: string,
    credentialId: string,
    isEmbedded: boolean,): Promise<IActionResult> {

    let updateRequest: Promise<any>;
    switch (credentialOption) {
      case 'password':

        updateRequest = updateConnections(
          connections,
          username,
          {
            isEmbedded: isEmbedded,
            newPassword: password
          });
        break;

      case 'oauth':
        updateRequest = DataConnections.updateConnectionsOAuth(connections.map(c => c.id), credentialId);
        break;

      case 'none':
        updateRequest = DataConnections.updateConnectionsOAuth(connections.map(c => c.id));
        break;

      default:
        throw 'Unexpected credential option: ' + credentialOption;
    }

    return updateRequest.then((result: IActionResult) => {
      notifyResult(result, connections);
      return result;
    });
  }

  function notifyResult(result: IResult, connections: EditableConnection[]) {
    // Edit connection does not have partial success - it is all-or-nothing
    // So, if any errors are reported, notify complete failure (with the first error message)
    if (result && result.errors && result.errors.length) {
      ContentActionNotification.displayFailureToast(errorMessage(result.errors[0]));
    } else {
      ContentActionNotification.displaySuccessToast(
        Messages.updateConnectionsSuccess({
          ITEM_COUNT: connections.length,
          ITEM_COUNT_LOC: Formatters.formatCount(connections.length)
        })
      );
    }
  }

  function errorMessage(error: IError): string {
    switch (error.code) {
      case CommonErrorCode.DatasourceNotFound:
      case CommonErrorCode.DataConnectionNotFound:
        return Messages.updateConnectionsError_datasourceNotFound();
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.updateConnectionsError_insufficientPermissions();
      case DataConnectionErrorCode.UpdateConnectionsInvalidPort:
        return Messages.updateConnectionsError_invalidPort();
      default:
        return Messages.errorUnknown();
    }
  }

  export function makePrompt(numberOfConnections: number) {
    return Messages.editDataConnections_prompt({
      ITEM_COUNT: numberOfConnections,
      ITEM_COUNT_LOC: Formatters.formatCount(numberOfConnections)
    });
  }

  export async function checkUsernamePasswordConnection(
    connectionId: string,
    editModel: IDataConnectionEditModelReact
  ): Promise<ITestConnectionResultReact> {
    const checkConnectionResult: ICheckConnectionResult | void =
      await DataConnections.checkConnection(connectionId, editModelToConnectionUpdate(editModel));
    return updateTestConnectionResult(checkConnectionResult);
  }

  function editModelToConnectionUpdate(editModel: IDataConnectionEditModelReact): IConnectionUpdate {
    return {
      server: editModel.serverName,
      port: editModel.serverPort,
      username: editModel.username,
      passwordUpdate: !editModel.password.isEmbedded ? undefined : editModel.password,
      usingRemoteQueryAgent: editModel.usingRemoteQueryAgent
    };
  }

  function updateTestConnectionResult(serverResponse: ICheckConnectionResult): ITestConnectionResultReact {
    return {
      isPending: false,
      succeeded: serverResponse.succeeded,
      messages: serverResponse.messages,
    };
  }

  function updateConnections(
    connections: EditableConnection[],
    username: string,
    password: {
      isEmbedded: boolean;
      newPassword: string;
    }
  ): Promise<IResult> {
    return DataConnections.updateConnections(connections.map(c => c.id), getConnectionUpdate(username, password));
  }

  function getConnectionUpdate(
    username: string,
    password: {
      isEmbedded: boolean;
      newPassword: string;
    }
  ): IConnectionUpdate {
    return {
      server: undefined,
      port: undefined,
      username: username,
      passwordUpdate: !password.isEmbedded ? undefined : password,
      usingRemoteQueryAgent: undefined
    };
  }
}
